import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import InvoiceDialog from "./InvoiceDialog";
import AccountingRecords from "./AccountingRecords";
import { fmtMoney, fmtShortDate, cardCls } from "./accountingUtils";

const FILTERS = [["all", "All"], ["unpaid", "Unpaid"], ["overdue", "Overdue"], ["paid", "Paid"], ["draft", "Drafts"], ["receipts", "Sales receipts"]];
const STATUS_CLS = { paid: "border-emerald-500/50 bg-emerald-500/15 text-emerald-300", overdue: "border-red-500/50 bg-red-500/15 text-red-300", partial: "border-amber-500/50 bg-amber-500/15 text-amber-300", draft: "border-white/20 bg-white/5 text-white/60", open: "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-[#8fa4f0]" };

/**
 * AccountingInvoices — Sales: invoices + sales receipts list with status
 * filters, plus a Customers sub-tab (generic records).
 */
const AccountingInvoices = ({ base, readOnly, viewUserId, defaultTaxRate, onChanged, openNew, clearOpenNew }) => {
  const confirm = useConfirm();
  const [tab, setTab] = useState("invoices");
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // {kind, initial}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { params: { user_id: viewUserId || undefined } };
      const [inv, cus, it] = await Promise.all([api.get(`${base}/invoices`, p), api.get(`${base}/customers`, p), api.get(`${base}/items`, p)]);
      setRows(inv.data || []); setCustomers(cus.data || []); setItems((it.data || []).filter((x) => x.active !== false));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load invoices"); } finally { setLoading(false); }
  }, [base, viewUserId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (openNew) { setDlg({ kind: openNew, initial: null }); clearOpenNew(); } }, [openNew, clearOpenNew]);

  const shown = rows.filter((r) => filter === "all" ? true : filter === "receipts" ? r.kind === "sales_receipt" : filter === "unpaid" ? ["open", "overdue", "partial"].includes(r.status) : r.status === filter);
  const total = shown.reduce((s, r) => s + (filter === "unpaid" || filter === "overdue" ? r.balance : r.total), 0);

  const remove = async (r) => {
    if (!(await confirm({ title: `Delete ${r.kind === "sales_receipt" ? "sales receipt" : "invoice"} #${r.number}?`, description: `${r.customer_name || "No customer"} · ${fmtMoney(r.total)}`, confirmLabel: "Delete" }))) return;
    try { await api.delete(`${base}/invoices/${r.id}`); toast.success("Deleted"); load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="acct-invoices">
      <div className="flex gap-1 rounded-md border border-white/10 bg-white/[0.02] p-1" role="tablist">
        {[["invoices", "Invoices"], ["customers", "Customers"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`acct-sales-tab-${k}`} className={`flex-1 rounded px-2 py-1.5 font-mono text-[11px] uppercase tracking-widest ${tab === k ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "customers" ? <AccountingRecords base={base} entity="customers" readOnly={readOnly} viewUserId={viewUserId} onChanged={load} /> : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
              {FILTERS.map(([k, l]) => <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`acct-inv-filter-${k}`} className={`shrink-0 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${filter === k ? "border-[#1a3fd1] bg-[#1a3fd1] text-white" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>)}
            </div>
            {!readOnly && (
              <>
                <button type="button" onClick={() => setDlg({ kind: "invoice", initial: null })} data-testid="acct-inv-new" className="inline-flex h-9 items-center gap-1 rounded-md bg-[#1a3fd1] px-3 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85"><Plus className="h-3.5 w-3.5" /> Invoice</button>
                <button type="button" onClick={() => setDlg({ kind: "sales_receipt", initial: null })} data-testid="acct-receipt-new" className="inline-flex h-9 items-center gap-1 rounded-md border border-white/15 bg-white/5 px-3 text-xs text-white/80 hover:border-white/25"><Plus className="h-3.5 w-3.5" /> Sales receipt</button>
              </>
            )}
          </div>
          <div className={`${cardCls} flex items-center justify-between`} data-testid="acct-inv-total">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">{shown.length} {filter === "receipts" ? "receipts" : "invoices"}{filter === "unpaid" || filter === "overdue" ? " · balance due" : ""}</span>
            <span className="font-mono text-lg font-bold text-white">{fmtMoney(total)}</span>
          </div>
          {loading ? <div className="flex justify-center py-10 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div> : shown.length === 0 ? (
            <div className={`${cardCls} py-10 text-center text-sm text-white/50`} data-testid="acct-inv-empty">Nothing here yet.</div>
          ) : (
            <ul className="divide-y divide-white/10 overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]">
              {shown.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`acct-inv-${r.id}`}>
                  <button type="button" onClick={() => !readOnly && setDlg({ kind: r.kind, initial: r })} className="min-w-0 flex-1 text-left" data-testid={`acct-inv-${r.id}-open`}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{r.kind === "sales_receipt" ? "Receipt" : "Invoice"} #{r.number} · {fmtShortDate(r.date)}{r.kind === "invoice" ? ` · due ${fmtShortDate(r.due_date)}` : ""}</div>
                    <div className="truncate text-sm font-semibold text-white">{r.customer_name || "No customer"}</div>
                    <div className="truncate text-xs text-white/55">{r.lines.map((l) => l.name).filter(Boolean).join(", ")}</div>
                  </button>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold text-white">{fmtMoney(r.total)}</div>
                    {r.balance > 0 && r.kind === "invoice" && <div className="font-mono text-[10px] text-white/50">due {fmtMoney(r.balance)}</div>}
                    <span className={`mt-0.5 inline-block rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${STATUS_CLS[r.status] || STATUS_CLS.open}`} data-testid={`acct-inv-${r.id}-status`}>{r.status}</span>
                  </div>
                  {!readOnly && <button type="button" onClick={() => remove(r)} data-testid={`acct-inv-${r.id}-delete`} aria-label="Delete" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {dlg && <InvoiceDialog base={base} kind={dlg.kind} initial={dlg.initial} customers={customers} items={items} defaultTaxRate={defaultTaxRate} onClose={() => setDlg(null)} onSaved={() => { load(); onChanged?.(); }} />}
    </div>
  );
};

export default AccountingInvoices;
